import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { NotificationBellDrawer } from './components/notifications/NotificationBellDrawer';
import {
  AnimatePresence as FramerAnimatePresence,
  motion,
  type AnimatePresenceProps,
} from 'framer-motion';
import {
  Activity,
  ArrowRight,
  BarChart3,
  Bell,
  BookOpen,
  Bookmark,
  Calendar,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Clock,
  FileText,
  Flame,
  GraduationCap,
  Home,
  Image as ImageIcon,
  LineChart,
  Lock,
  LogOut,
  Mail,
  MessageCircle,
  Mic,
  Monitor,
  Percent,
  Play,
  Search,
  Send,
  Settings,
  Shield,
  Sparkles,
  Target,
  User,
  Users,
  Volume2,
  Copy,
  Plus,
  Trash2,
  ThumbsUp,
  ThumbsDown,
  Upload,
  Crown,
  PlayCircle,
  Bot,
  RotateCcw,
  Star,
  Zap,
  Brain,
  Check,
  MoreVertical,
  Wand2,
  HelpCircle,
  PenTool,
  BarChart2,
} from 'lucide-react';
import { sendAiChatMessage, getAiUsage, AiChatMessage, AiUsageData, simulateUpgrade, simulateAddCredits, simulateResetUsage, createRazorpayCreditsOrder, verifyRazorpayCreditsPayment, getAiSessions, saveAiSession, deleteAiSession } from './api/ai';
import { ApiError } from './api/client';
import { RichText } from './components/RichText';
import type { LucideIcon } from 'lucide-react';
import './styles/global.css';
import './styles/screens.css';
import './screens/ai/aiWorkspace.css';

import type { AcademicSubject } from './types';
import { useAcademicSubjects } from './hooks/useAcademic';
import { useUnreadCount } from './hooks/useNotifications';
import { HERO_PREVIEW } from './api/mock/dashboardMock';
import { EmptyState, ErrorState, LoadingState } from './components/states/StateViews';
import { Illustration } from './components/common/Illustration';
import { ErrorPage } from './components/ErrorPage';
import { useAuth } from './context/AuthProvider';
import { RequireAuth } from './components/auth/RequireAuth';
import { LoginScreen } from './screens/LoginScreen';
import { RegisterScreen } from './screens/RegisterScreen';
import { ForgotPasswordScreen } from './screens/ForgotPasswordScreen';
import { ResetPasswordScreen } from './screens/ResetPasswordScreen';
import { RequireRole } from './components/auth/RequireRole';
import { AdminShell } from './components/admin/AdminShell';
import { dashboardPathForRole } from './lib/roles';
import { navForRole } from './config/navigation';
import { DOMAIN } from './config';
import { analyticsEvents } from './lib/firebase';
import { usePushRegistration } from './hooks/usePushRegistration';
import { useAndroidBackButton } from './hooks/useAndroidBackButton';
import { useHomepageContent } from './hooks/useHomepage';
import { useSeo } from './hooks/useSeo';
import { CookieConsent } from './components/CookieConsent';
import { PwaPrompts } from './components/PwaPrompts';
import { FloatingDock } from './components/navigation/FloatingDock';
import { SectionSwitcher } from './components/SectionSwitcher';

// framer-motion 11 types AnimatePresence as returning `JSX.Element | undefined`,
// which React 18's JSX rules reject (only `null` is allowed). Re-type it as a
// valid function component so it can be used as a JSX element (TS2786 fix).
const AnimatePresence =
  FramerAnimatePresence as React.FC<React.PropsWithChildren<AnimatePresenceProps>>;

// Route-level code splitting (PRD Modules 7.1/7.2). The public shell + auth
// screens stay in the main bundle; everything behind a click (study, tests, CMS,
// admin, legal) loads on demand. `lazy` needs a default export, hence the .then.
const lazyNamed = <T extends Record<string, unknown>>(
  loader: () => Promise<T>,
  name: keyof T,
) =>
  React.lazy(() =>
    loader()
      .then((m) => ({ default: m[name] as React.ComponentType }))
      .catch((err) => {
        const isChunkError =
          err?.name === 'ChunkLoadError' ||
          /Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed/i.test(
            err?.message || '',
          );
        const hasReloaded = sessionStorage.getItem('webfoxx_chunk_reload') === '1';
        if (isChunkError && !hasReloaded) {
          sessionStorage.setItem('webfoxx_chunk_reload', '1');
          window.location.reload();
        }
        throw err;
      }),
  );

const NotificationsScreen = lazyNamed(() => import('./screens/NotificationsScreen'), 'NotificationsScreen');
const SyllabusScreen = lazyNamed(() => import('./screens/academic/SyllabusScreen'), 'SyllabusScreen');
const SubjectBrowseScreen = lazyNamed(() => import('./screens/academic/SubjectBrowseScreen'), 'SubjectBrowseScreen');
const TopicWorkspaceScreen = lazyNamed(() => import('./screens/academic/TopicWorkspaceScreen'), 'TopicWorkspaceScreen');
const AcademicSearchScreen = lazyNamed(() => import('./screens/academic/AcademicSearchScreen'), 'AcademicSearchScreen');
const CmsScreen = lazyNamed(() => import('./screens/cms/CmsScreen'), 'CmsScreen');
const DocumentEditorScreen = lazyNamed(
  () => import('./screens/cms/DocumentEditorScreen'),
  'DocumentEditorScreen',
);
const StudentDashboard = lazyNamed(() => import('./screens/StudentDashboard'), 'StudentDashboard');
const PricingScreen = lazyNamed(() => import('./screens/PricingScreen'), 'PricingScreen');
const RevisionWorkspace = lazyNamed(() => import('./screens/RevisionWorkspace'), 'RevisionWorkspace');
const BookmarksScreen = lazyNamed(() => import('./screens/BookmarksScreen'), 'BookmarksScreen');
const SupportScreen = lazyNamed(() => import('./screens/SupportScreen'), 'SupportScreen');
const AssessmentDashboard = lazyNamed(() => import('./screens/assessment/AssessmentDashboard'), 'AssessmentDashboard');
const TestWorkspace = lazyNamed(() => import('./screens/assessment/TestWorkspace'), 'TestWorkspace');
const TestResult = lazyNamed(() => import('./screens/assessment/TestResult'), 'TestResult');
const AssessmentAnalytics = lazyNamed(() => import('./screens/assessment/AssessmentAnalytics'), 'AssessmentAnalytics');
const ProfileCenter = lazyNamed(() => import('./screens/profile/ProfileCenter'), 'ProfileCenter');
const ContentDashboard = lazyNamed(() => import('./screens/RoleDashboards'), 'ContentDashboard');
const FacultyDashboard = lazyNamed(() => import('./screens/RoleDashboards'), 'FacultyDashboard');
const AdminConsole = lazyNamed(() => import('./screens/admin/AdminConsole'), 'AdminConsole');
const PrivacyPolicy = lazyNamed(() => import('./screens/legal/LegalPages'), 'PrivacyPolicy');
const TermsOfService = lazyNamed(() => import('./screens/legal/LegalPages'), 'TermsOfService');
const RefundPolicy = lazyNamed(() => import('./screens/legal/LegalPages'), 'RefundPolicy');

import { SubjectsWorkspace } from './screens/academic/SubjectsWorkspace';

type Feature = { title: string; text: string; icon: LucideIcon; tone: string; path: string; illustrationContext?: string };

/** Primary nav for the signed-in role (students/guests get the student list). */
function useNavItems() {
  const { user } = useAuth();
  const role = user?.role;
  return useMemo(() => {
    const baseNav = navForRole(role);
    if (user) {
      return baseNav.map((item) =>
        item.path === '/subjects' ? { ...item, label: 'My Subjects', short: 'My Subjects' } : item
      );
    }
    return baseNav;
  }, [user, role]);
}

/** The signed-in user's own dashboard ('/admin', '/faculty', '/content', '/dashboard'). */
function useDashboardPath(): string {
  const { user } = useAuth();
  return dashboardPathForRole(user?.role ?? 'student');
}

const supportingModules: Feature[] = [
  {
    title: 'Structured Learning',
    text: 'Unit-wise paths with progress, checkpoints, and revision loops.',
    icon: GraduationCap,
    tone: 'blue',
    path: '/syllabus',
    illustrationContext: 'feature.timeline',
  },
  {
    title: 'Curated Notes',
    text: 'Clean notes, definitions, and examples built for fast recall.',
    icon: FileText,
    tone: 'orange',
    path: '/syllabus',
    illustrationContext: 'card.document',
  },
  {
    title: 'Chapter Practice',
    text: 'Practice questions mapped to chapters and past exam patterns.',
    icon: Target,
    tone: 'green',
    path: '/assessment',
    illustrationContext: 'card.mock-test',
  },
  {
    title: 'Weakness Analytics',
    text: 'Find low-accuracy units and turn mistakes into revision tasks.',
    icon: LineChart,
    tone: 'red',
    path: '/analytics',
    illustrationContext: 'hero.results',
  },
  {
    title: 'Cross Device Sync',
    text: 'Continue learning across phone, tablet, laptop, and desktop.',
    icon: Monitor,
    tone: 'slate',
    path: '/dashboard',
    illustrationContext: 'page.admit-cards',
  },
  {
    title: 'Distraction Free',
    text: 'Focused reading, test, and video surfaces with calmer controls.',
    icon: Shield,
    tone: 'blue',
    path: '/assessment',
    illustrationContext: 'feature.vault',
  },
  {
    title: 'Active Learning',
    text: 'AI prompts, flashcards, and drills keep your recall active.',
    icon: Sparkles,
    tone: 'orange',
    path: '/ai',
    illustrationContext: 'hero.home',
  },
];

const footerColumns: Array<{ title: string; links: Array<{ label: string; path: string }> }> = [
  {
    title: 'Company',
    links: [
      { label: 'Contact support', path: '/support' },
      { label: 'Pricing', path: '/pricing' },
    ],
  },
  {
    title: 'Product',
    links: [
      { label: 'Subjects', path: '/subjects' },
      { label: 'AI Workspace', path: '/ai' },
      { label: 'Mock Tests', path: '/assessment' },
      { label: 'Dashboard', path: '/dashboard' },
    ],
  },
  {
    title: 'Resources',
    links: [
      { label: 'Previous Year Papers', path: '/assessment' },
      { label: 'Study Material', path: '/syllabus' },
      { label: 'Syllabus', path: '/subjects' },
      { label: 'Revision', path: '/revision' },
    ],
  },
  {
    title: 'Support',
    links: [
      { label: 'Help Center', path: '/support' },
      { label: 'Privacy Policy', path: '/privacy' },
      { label: 'Terms', path: '/terms' },
      { label: 'Refund Policy', path: '/refund' },
    ],
  },
];

const aiMessages = [
  {
    role: 'assistant',
    text: 'Good morning. I found three weak General Aptitude units from your last mock: Numerical Ability, Logical Reasoning, and General Awareness.',
  },
  { role: 'user', text: 'Create a 45-minute revision plan for General Aptitude Test (GAT 501).' },
  {
    role: 'assistant',
    text: 'Start with quantitative formulas, then solve 10 PYQs on speed-distance-time, then review current affairs with a short active-recall quiz.',
  },
];

const testOptions = [
  'It measures association between two variables.',
  'It confirms causation between two variables.',
  'It is used only for qualitative research.',
  'It removes sampling error from a study.',
];

function App() {
  useAndroidBackButton();
  return (
    <React.Suspense fallback={<div className="adaptive-page"><LoadingState label="Loading…" /></div>}>
    <Routes>
      <Route element={<Layout />}>
        {/* Public routes — accessible to guests */}
        <Route index element={<HomeScreen />} />
        <Route path="subjects" element={<SubjectsWorkspace />} />
        {/* Legacy subject-detail URL: the real browse screen is /syllabus/:slug. */}
        <Route path="subjects/:id" element={<Navigate to="/syllabus" replace />} />

        {/* Phase 2 — Academic Foundation: syllabus browsing + Topic Workspace */}
        <Route path="syllabus" element={<SyllabusScreen />} />
        <Route path="syllabus/:slug" element={<SubjectBrowseScreen />} />
        <Route path="topic/:id" element={<TopicWorkspaceScreen />} />
        <Route path="search" element={<AcademicSearchScreen />} />
        <Route path="ai" element={<AIWorkspace />} />
        <Route path="mock-tests" element={<Navigate to="/assessment" replace />} />
        {/* These features shipped inside other screens rather than as standalone
            pages (notes/mind-maps live in the Topic Workspace, flashcards in
            Revision, PYQ tests in Assessment). The old placeholder URLs are kept
            as redirects so existing links and bookmarks still land somewhere real.
            /about, /careers and /blog were removed outright — there is no content
            for them, and a "coming soon" page is precisely what 7.15 forbids. */}
        <Route path="notes" element={<Navigate to="/syllabus" replace />} />
        <Route path="mind-maps" element={<Navigate to="/syllabus" replace />} />
        <Route path="flashcards" element={<Navigate to="/revision" replace />} />
        <Route path="chapter-practice" element={<Navigate to="/assessment" replace />} />
        <Route path="pyqs" element={<Navigate to="/assessment" replace />} />
        <Route path="contact" element={<Navigate to="/support" replace />} />
        <Route path="help" element={<Navigate to="/support" replace />} />
        <Route path="privacy" element={<PrivacyPolicy />} />
        <Route path="terms" element={<TermsOfService />} />
        <Route path="refund" element={<RefundPolicy />} />
        <Route path="pricing" element={<PricingScreen />} />

        {/* Auth routes */}
        <Route path="login" element={<LoginScreen />} />
        <Route path="register" element={<RegisterScreen />} />
        <Route path="forgot-password" element={<ForgotPasswordScreen />} />
        <Route path="reset-password" element={<ResetPasswordScreen />} />

        {/* Protected routes — require an authenticated session */}
        <Route element={<RequireAuthLayout />}>
          <Route path="dashboard" element={<StudentDashboard />} />
          <Route path="learn" element={<LearnPage />} />
          <Route path="notifications" element={<NotificationsScreen />} />
          <Route path="analytics" element={<AssessmentAnalytics />} />
          <Route path="revision" element={<RevisionWorkspace />} />
          <Route path="revision-planner" element={<Navigate to="/revision" replace />} />
          <Route path="bookmarks" element={<BookmarksScreen />} />
          <Route path="support" element={<SupportScreen />} />
          <Route path="assessment" element={<AssessmentDashboard />} />
          <Route path="test/:id/result" element={<TestResult />} />
          <Route path="profile" element={<ProfileCenter />} />
          <Route path="profile/:section" element={<ProfileCenter />} />
          <Route path="settings" element={<ProfileCenter />} />
        </Route>

        <Route path="*" element={<ErrorPage code={404} />} />
      </Route>

      {/* Unified Admin & CMS Shell Layout (Outside public student Layout) */}
      <Route
        element={
          <RequireRole roles={['admin', 'faculty', 'content']}>
            <AdminShell />
          </RequireRole>
        }
      >
        <Route path="admin" element={<AdminConsole />} />
        <Route path="admin/*" element={<AdminConsole />} />
        <Route path="faculty" element={<FacultyDashboard />} />
        <Route path="content" element={<ContentDashboard />} />
        <Route path="cms/topics/:topicId/document" element={<DocumentEditorScreen />} />
        <Route path="cms" element={<CmsScreen />} />
        <Route path="cms/*" element={<CmsScreen />} />
      </Route>

      {/* Full-screen, protected mock-test workspace (outside the shell) */}
      <Route
        path="mock"
        element={
          <RequireAuth>
            <MockTestPage />
          </RequireAuth>
        }
      />
      <Route
        path="mock/:id"
        element={
          <RequireAuth>
            <MockTestPage />
          </RequireAuth>
        }
      />
      {/* Full-screen assessment workspace (Phase 5, outside the shell) */}
      <Route
        path="test/:id"
        element={
          <RequireAuth>
            <TestWorkspace />
          </RequireAuth>
        }
      />
    </Routes>
    </React.Suspense>
  );
}

function RequireAuthLayout() {
  return (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  );
}

function Layout() {
  const location = useLocation();
  const isLearning = location.pathname === '/learn';
  const isAi = location.pathname === '/ai';
  usePushRegistration();
  const { user } = useAuth();
  // Drives the shell accent (nav pills, bottom bar) so the whole chrome takes on
  // the role's identity, not just the dashboard body.
  const shellRole = user?.role ?? undefined;
  return (
    <div className="app-shell min-h-screen" data-role={shellRole}>
      <a className="skip-link" href="#main">Skip to content</a>
      <DesktopNavigation />
      <MobileTopBar />
      <AnimatePresence mode="wait">
        <motion.main
          key={location.pathname}
          id="main"
          tabIndex={-1}
          className={`app-main ${isLearning ? 'is-learning' : ''} ${isAi ? 'is-ai' : ''}`}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
        >
          <Outlet />
        </motion.main>
      </AnimatePresence>
      <BottomNavigation />
      <CookieConsent />
      <PwaPrompts />
    </div>
  );
}

/**
 * Marks exactly one nav item active: the longest path that prefixes the current
 * URL. Staff navs contain nested paths ('/cms' and '/cms/questions'), where a
 * plain startsWith would light up both pills at once.
 */
function useActiveNavPath(items: Array<{ path: string }>): string | null {
  const location = useLocation();
  const { pathname } = location;
  return useMemo(() => {
    const matches = items.filter((item) =>
      item.path === '/' ? pathname === '/' : pathname === item.path || pathname.startsWith(`${item.path}/`),
    );
    if (matches.length === 0) return null;
    return matches.reduce((best, item) => (item.path.length > best.path.length ? item : best)).path;
  }, [items, pathname]);
}

function DesktopNavigation() {
  const navigate = useNavigate();
  const { status } = useAuth();
  const items = useNavItems();
  const activePath = useActiveNavPath(items);
  // Show the account UI while a session is being verified too, to avoid a
  // "Log in" flash for returning users with a valid token.
  const showAccount = status !== 'guest';
  const { data: unread = 0 } = useUnreadCount(showAccount);
  return (
    <header className="desktop-nav">
      <div className="nav-inner">
        <button className="brand-button" onClick={() => navigate('/')} aria-label="Open home">
          <img src="/assets/webfoxx_academy_logo.png" alt="" className="brand-mark" />
          <span>WebFoxx Academy</span>
        </button>
        <nav className="desktop-nav-links" aria-label="Primary navigation">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.path}
                className={`nav-pill ${activePath === item.path ? 'is-active' : ''}`}
                aria-current={activePath === item.path ? 'page' : undefined}
                onClick={() => navigate(item.path)}
              >
                <Icon size={17} aria-hidden="true" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="nav-actions">
          {showAccount ? (
            <>
              <NotificationBellDrawer />
              <ProfileButton />
            </>
          ) : (
            <button className="primary-action compact" onClick={() => navigate('/login')}>
              Log in
            </button>
          )}
        </div>
      </div>
    </header>
  );
}

function MobileTopBar() {
  const location = useLocation();
  const items = useNavItems();
  const current = items.find((item) =>
    item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path),
  );
  const title = !current || current.path === '/' ? 'WebFoxx Academy' : current.label;
  const navigate = useNavigate();
  const { status } = useAuth();
  const showAccount = status !== 'guest';
  return (
    <header className="mobile-appbar">
      <div className="mobile-brand">
        <img src="/assets/webfoxx_academy_logo.png" alt="" className="mobile-brand-mark" />
        <span>{title}</span>
      </div>
      {showAccount ? (
        <ProfileButton compact />
      ) : (
        <button className="primary-action compact" onClick={() => navigate('/login')}>
          Log in
        </button>
      )}
    </header>
  );
}

function ProfileButton({ compact = false }: { compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const menu: Array<{ label: string; icon: LucideIcon; onClick: () => void }> = [
    { label: 'Account', icon: User, onClick: () => navigate('/profile') },
    { label: 'Notifications', icon: Bell, onClick: () => navigate('/notifications') },
    { label: 'Support', icon: MessageCircle, onClick: () => navigate('/support') },
    { label: 'Settings', icon: Settings, onClick: () => navigate('/profile/appearance') },
    {
      label: 'Logout',
      icon: LogOut,
      onClick: () => {
        logout();
        navigate('/');
      },
    },
  ];

  return (
    <div className="profile-wrap">
      <button
        className={`avatar-button ${compact ? 'is-compact' : ''}`}
        onClick={() => setOpen((value) => !value)}
        aria-label="Open profile menu"
        aria-expanded={open}
      >
        <User size={compact ? 18 : 19} aria-hidden="true" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            className="profile-menu"
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.16 }}
          >
            {user && (
              <div className="profile-menu-head">
                <strong>{user.name}</strong>
                <span>{user.email}</span>
              </div>
            )}
            {menu.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.label}
                  className="profile-menu-item"
                  onClick={() => {
                    setOpen(false);
                    item.onClick();
                  }}
                >
                  <Icon size={17} aria-hidden="true" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function BottomNavigation() {
  const navigate = useNavigate();
  const items = useNavItems();
  const activePath = useActiveNavPath(items);
  return (
    <FloatingDock
      items={items}
      activePath={activePath}
      onNavigate={(path) => navigate(path)}
    />
  );
}

function HomeScreen() {
  const navigate = useNavigate();
  const { status } = useAuth();
  const dashboardPath = useDashboardPath();
  // Hero copy is editable from the admin Homepage CMS (PRD 6.19); the literals
  // below are the fallback while it loads or if the request fails.
  const { data: cms } = useHomepageContent();
  const hero = cms?.hero;
  const titleLines = (hero?.title ?? 'Prepare smarter.\nScore Higher\nQualify with Confidence...').split('\n');
  useSeo({
    title: 'WebFoxx Academy | CUET UG & PG Preparation',
    description: hero?.subtitle ?? 'Structured learning, chapter practice, mock tests, revision tools, and performance analytics for CUET UG & PG aspirants.',
  });
  return (
    <section className="home-screen adaptive-page">
      <div className="hero-layout">
        <div className="hero-copy">
          <div className="hero-marketing hero-marketing--wide">
            <div className="eyebrow">
              <Sparkles size={15} aria-hidden="true" />
              {hero?.eyebrow ?? 'Built for NTA CUET UG & PG Aspirants'}
            </div>
            <h1>
              {titleLines.map((line, index) => (
                <span
                  className={`hero-title-line ${index === titleLines.length - 1 && titleLines.length > 1 ? 'is-accent' : ''}`}
                  key={`${line}-${index}`}
                >
                  {line}
                </span>
              ))}
            </h1>
            <p>
              {hero?.subtitle ?? `Master every subject with chapter-wise learning, previous year questions, unlimited mock tests, AI-powered guidance, smart revision tools, and detailed performance analytics—all in one platform designed exclusively for NTA CUET UG & PG aspirants.`}
            </p>
            <div className="hero-actions">
              <button
                className="primary-action btn-glowing-shimmer"
                onClick={() => navigate(status === 'guest' ? '/register' : dashboardPath)}
              >
                {hero?.ctaText ?? 'Start Preparing Free'}
                <ArrowRight size={18} aria-hidden="true" />
              </button>
              <button className="secondary-action" onClick={() => navigate('/subjects')}>
                Explore Subjects
              </button>
            </div>
            <div className="trust-row" aria-label="Trust indicators">
              {['Always Free', 'No Credit Card Required', 'Cancel Anytime'].map((item) => (
                <span key={item}>
                  <CheckCircle2 size={16} aria-hidden="true" />
                  {item}
                </span>
              ))}
            </div>
          </div>

          <div className="hero-marketing hero-marketing--mobile">
            <div className="eyebrow hero-eyebrow--mobile">
              <Sparkles size={13} aria-hidden="true" />
              Built for NTA CUET UG & PG
            </div>
            <h1>
              <span className="hero-title-line">Prepare Smarter.</span>
              <span className="hero-title-line is-accent">Crack CUET with Confidence.</span>
            </h1>
            <p className="hero-subtitle--mobile">
              <span className="hero-tagline">PYQs • Mock Tests • AI Guidance</span>
              Everything in one platform.
            </p>
            <div className="hero-actions hero-actions--mobile-only">
              <button
                className="primary-action btn-glowing-shimmer"
                onClick={() => navigate(status === 'guest' ? '/register' : dashboardPath)}
              >
                {hero?.ctaText ?? 'Start Preparing Free'}
                <ArrowRight size={18} aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>

        <div className="hero-product">
          {/* Decorative backdrop elements behind desktop setup */}
          <div className="hero-backdrop-glows" aria-hidden="true">
            <div className="backdrop-glow glow-primary" />
            <div className="backdrop-glow glow-secondary" />
            <div className="backdrop-grid-pattern" />
          </div>

          {/* Photorealistic 3D iMac Desktop Computer Standing on Desk */}
          <div className="imac-desktop-setup hero-device--desktop">
            {/* Single Clean iMac Monitor Frame */}
            <div className="imac-monitor-frame">
              {/* Floating Glass Badges Row on the left side at the same height */}
              <div className="hero-floating-badges-left-row">
                <div className="hero-floating-badge badge-rank">
                  <span className="tooltip-bubble">Verified NTA Top 0.2% Scorers</span>
                  <Sparkles size={16} className="badge-icon-sparkle" />
                  <div className="badge-text-group">
                    <span>NTA CUET Rank</span>
                    <strong>99.8th Percentile</strong>
                  </div>
                </div>
                <div className="hero-floating-badge badge-streak">
                  <span className="tooltip-bubble">Daily Practice Milestone</span>
                  <span className="badge-pulse-dot" />
                  <div className="badge-text-group">
                    <span>Active Study Streak</span>
                    <strong style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      {HERO_PREVIEW.streakDays} Days
                      <Flame size={14} style={{ color: '#FF7D1E', filter: 'drop-shadow(0 0 4px rgba(255, 125, 30, 0.4))' }} />
                    </strong>
                  </div>
                </div>
              </div>

              {/* Webcam Lens */}
              <div className="imac-webcam-lens" aria-hidden="true" />
              
              {/* Screen Glare Reflection Overlay */}
              <div className="imac-screen-glare" aria-hidden="true" />

              {/* Browser Display Content */}
              <DashboardPreviewCard />

              {/* Iconic Aluminum Bottom Chin */}
              <div className="imac-aluminum-chin" aria-hidden="true">
                <span className="chin-logo-dot" />
                <span className="chin-brand-text">WebFoxx Studio Display</span>
              </div>
            </div>

            {/* Apple-Style Tapered Aluminum L-Stand */}
            <div className="imac-l-stand" aria-hidden="true">
              <div className="stand-pillar" />
              <div className="stand-foot-plate" />
            </div>

            {/* Physical Desk Surface & Peripheral Accessories */}
            <div className="desk-surface-slab" aria-hidden="true">
              <div className="desk-mat-area">
                {/* Magic Keyboard */}
                <div className="magic-keyboard">
                  <div className="keyboard-keys-row">
                    {Array.from({ length: 15 }).map((_, i) => (
                      <span key={i} className="key-cap" />
                    ))}
                  </div>
                  <span className="spacebar-cap" />
                </div>

                {/* Magic Mouse */}
                <div className="magic-mouse">
                  <span className="mouse-seam" />
                </div>
              </div>

              <div className="desk-bevel-edge" />
              <div className="desk-ambient-shadow" />
            </div>
          </div>

          <div className="hero-phone-setup hero-mobile-only">
            <div className="phone-device-frame">
              <div className="phone-device-screen">
                <div className="phone-device-island" aria-hidden="true" />
                <PhoneDeviceStatusBar />
                <MobileDashboardPreview />
              </div>
            </div>
          </div>

          <div className="hero-tablet-setup hero-tablet-only">
            <div className="tablet-device-frame">
              <div className="tablet-device-screen">
                <DashboardPreviewCard />
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          className="hero-explore-link hero-mobile-only text-action"
          onClick={() => navigate('/subjects')}
        >
          Explore Subjects
          <ArrowRight size={15} aria-hidden="true" />
        </button>

        <div className="hero-value-row hero-mobile-only" aria-label="Platform highlights">
          {['25,000+ Questions', 'Full-Length Mock Tests', 'PYQs with Solutions'].map((item) => (
            <span key={item}>
              <CheckCircle2 size={15} aria-hidden="true" />
              {item}
            </span>
          ))}
        </div>

        <p className="hero-trust-caption hero-mobile-only">Trusted by CUET Aspirants</p>
      </div>

      <WorkspaceModules />
      <SiteFooter />
    </section>
  );
}

function SiteFooter() {
  const dashboardPath = useDashboardPath();
  const navigate = useNavigate();
  return (
    <footer className="site-footer">
      <div className="footer-grid">
        <div className="footer-brand">
          <button
            className="brand-button"
            onClick={() => navigate('/')}
            aria-label="WebFoxx Academy home"
          >
            <img src="/assets/webfoxx_academy_logo.png" alt="" className="brand-mark" />
            <span>WebFoxx Academy</span>
          </button>
          <p>The premium workspace built exclusively for CUET UG & PG aspirants.</p>
        </div>

        {footerColumns.map((column) => (
          <nav className="footer-col" key={column.title} aria-label={column.title}>
            <h4>{column.title}</h4>
            {column.links.map((link) => (
              <button
                key={link.label}
                className="footer-link"
                onClick={() => navigate(link.path === '/dashboard' ? dashboardPath : link.path)}
              >
                {link.label}
              </button>
            ))}
          </nav>
        ))}

        <div className="footer-newsletter">
          <h4>Newsletter</h4>
          <p>Study tips and exam updates, no spam.</p>
          <form className="newsletter-form" onSubmit={(event) => event.preventDefault()}>
            <label className="newsletter-field">
              <Mail size={17} aria-hidden="true" />
              <span className="sr-only">Email address</span>
              <input type="email" placeholder="you@example.com" required />
            </label>
            <button type="submit" className="primary-action compact">
              Subscribe
            </button>
          </form>
        </div>
      </div>
      <div className="footer-bottom">
        <span>© 2026 WebFoxx Academy. All rights reserved.</span>
        <span>Built for serious NTA CUET & CUET (PG) aspirants.</span>
      </div>
    </footer>
  );
}

function WorkspaceModules() {
  const navigate = useNavigate();
  return (
    <section className="modules-section" aria-labelledby="modules-title">
      <div className="section-heading">
        <div>
          <span className="section-kicker">Workspace Modules</span>
          <h2 id="modules-title">Everything a serious NTA CUET & CUET (PG) routine needs.</h2>
        </div>
      </div>

      <div className="modules-layout">
        <motion.article
          className="module-featured"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.3 }}
        >
          <div className="module-featured-copy">
            <span className="module-tag">Featured module</span>
            <div className="feature-icon tone-purple-icon">
              <ClipboardList size={22} aria-hidden="true" />
            </div>
            <h3>Full Mock Tests</h3>
            <p>
              Sit timed, exam-mode tests with palette navigation, instant scoring, and a detailed
              explanation for every question — the closest thing to the real CUET UG & PG.
            </p>
            <ul className="module-points">
              {[
                'Live timer & auto-submit',
                'Question palette navigation',
                'Performance summary',
                'Detailed explanations',
              ].map((point) => (
                <li key={point}>
                  <CheckCircle2 size={16} aria-hidden="true" />
                  {point}
                </li>
              ))}
            </ul>
            <button className="text-action" onClick={() => navigate('/mock-tests')}>
              Explore mock tests
              <ArrowRight size={16} aria-hidden="true" />
            </button>
          </div>

          <div className="module-featured-preview" aria-hidden="true">
            <div className="mini-mock">
              <div className="mini-mock-top">
                <span className="mini-timer">
                  <Clock size={13} />
                  42:18
                </span>
                <span className="mini-count">Q 12 / 50</span>
              </div>
              <p className="mini-question">Primary purpose of correlation analysis?</p>
              <div className="mini-options">
                <span className="is-correct">A · Measures association</span>
                <span>B · Confirms causation</span>
                <span>C · Qualitative only</span>
              </div>
              <div className="mini-palette">
                {Array.from({ length: 15 }, (_, i) => (
                  <span key={i} className={i === 11 ? 'is-active' : i < 11 ? 'is-done' : ''} />
                ))}
              </div>
            </div>
          </div>
        </motion.article>

        <div className="module-grid">
          {supportingModules.map((module, index) => {
            const Icon = module.icon;
            return (
              <motion.article
                className={`module-card uiverse-card-holographic tone-${module.tone}`}
                key={module.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ delay: index * 0.03 }}
              >
                <div className="card-glare-overlay" aria-hidden="true" />
                <div className="feature-icon">
                  <Icon size={20} aria-hidden="true" />
                </div>
                <h3>{module.title}</h3>
                <p>{module.text}</p>
                <button className="text-action" onClick={() => navigate(module.path)}>
                  Learn more
                  <ArrowRight size={15} aria-hidden="true" />
                </button>
                {module.illustrationContext && (
                  <Illustration
                    context={module.illustrationContext}
                    size="xs"
                    style={{
                      position: 'absolute',
                      right: '12px',
                      bottom: '12px',
                      opacity: 0.85,
                      width: '80px',
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function formatPhoneDeviceTime(date: Date) {
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function PhoneDeviceStatusBar({ className = 'phone-device-status' }: { className?: string }) {
  const [time, setTime] = useState(() => formatPhoneDeviceTime(new Date()));

  useEffect(() => {
    const update = () => setTime(formatPhoneDeviceTime(new Date()));
    update();
    const id = window.setInterval(update, 10_000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className={className} aria-hidden="true">
      <span>{time}</span>
      <span className="phone-device-status-icons">
        <span />
        <span />
        <span />
      </span>
    </div>
  );
}

function MobileDashboardPreview() {
  const preview = HERO_PREVIEW;
  const firstName = preview.name.split(' ')[0] || preview.name;
  const overall = preview.todaysGoalPercent;
  const readiness = preview.averageAccuracyPercent;
  const heatmap = [0, 1, 2, 3, 2, 4, 3, 1, 2, 3, 4, 2, 1, 0, 2, 3, 4, 3, 2, 1, 0, 1, 2, 3, 4, 3, 2, 1];

  const quickActions = [
    { label: 'Practice', icon: Target, tone: 'green' },
    { label: 'Mock Test', icon: ClipboardList, tone: 'purple' },
    { label: 'Revision', icon: RotateCcw, tone: 'red' },
    { label: 'AI Tutor', icon: Bot, tone: 'blue' },
  ] as const;

  return (
    <article className="mobile-app-preview">
      <div className="mp-dash-scroll">
        <div className="mp-hero">
          <span className="mp-kicker">{preview.greeting}, {firstName}</span>
          <h2>Ready to achieve something great today?</h2>
          <p className="mp-goal">
            Preparing for: <strong>{preview.continueTopic}</strong>
          </p>
          <div className="mp-bar" aria-hidden="true">
            <span style={{ width: `${overall}%` }} />
          </div>
          <div className="mp-meta-row">
            <span className="mp-meta-pill">
              <Flame size={10} aria-hidden="true" />
              {preview.streakDays}d streak
            </span>
            <span className="mp-meta-pill">
              <Star size={10} aria-hidden="true" />
              {readiness}% ready
            </span>
          </div>

          <div className="mp-hero-continue">
            <h3>Continue Learning</h3>
            <p>{preview.continueTopic}</p>
            <div className="mp-bar" aria-hidden="true">
              <span style={{ width: `${overall}%` }} />
            </div>
            <small>Today&apos;s goal · 18/25 questions</small>
            <span className="mp-cta">Continue Studying</span>
          </div>
        </div>

        <div className="mp-quick-grid">
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <div className={`mp-quick-action tone-${action.tone}`} key={action.label}>
                <span className="mp-quick-icon">
                  <Icon size={12} aria-hidden="true" />
                </span>
                <span>{action.label}</span>
              </div>
            );
          })}
        </div>

        <div className="mp-card">
          <h3><Play size={11} aria-hidden="true" /> Continue where you left off</h3>
          <div className="mp-continue-item">
            <span className="mp-continue-icon"><BookOpen size={11} aria-hidden="true" /></span>
            <span className="mp-continue-copy">
              <strong>{preview.continueTopic}</strong>
              <small>Unit 3 · Logical Reasoning</small>
            </span>
          </div>
          <div className="mp-continue-item">
            <span className="mp-continue-icon"><ClipboardList size={11} aria-hidden="true" /></span>
            <span className="mp-continue-copy">
              <strong>Recent test activity</strong>
              <small>240 questions attempted</small>
            </span>
          </div>
        </div>

        <div className="mp-card">
          <h3><BarChart3 size={11} aria-hidden="true" /> Preparation progress</h3>
          <div className="mp-subject-grid">
            <div className="mp-subject-card">
              <span>General Aptitude</span>
              <strong>68%</strong>
              <small>12/18 topics</small>
            </div>
            <div className="mp-subject-card">
              <span>Domain / PG</span>
              <strong>54%</strong>
              <small>9/16 topics</small>
            </div>
          </div>
        </div>

        <div className="mp-card">
          <h3><BarChart3 size={11} aria-hidden="true" /> Performance</h3>
          <div className="mp-perf-grid">
            <div className="mp-perf-card"><span>Questions</span><strong>240</strong></div>
            <div className="mp-perf-card"><span>Accuracy</span><strong>{preview.averageAccuracyPercent}%</strong></div>
            <div className="mp-perf-card"><span>Streak</span><strong>{preview.streakDays}d</strong></div>
            <div className="mp-perf-card"><span>Topics</span><strong>21</strong></div>
          </div>
        </div>

        <div className="mp-card">
          <h3><Brain size={11} aria-hidden="true" /> Weak areas</h3>
          {preview.weakUnits.map((unit, index) => (
            <div className="mp-weak-row" key={unit}>
              <span>{unit}</span>
              <small>{58 + index * 7}% accuracy</small>
              <div className="mp-weak-bar" aria-hidden="true">
                <span style={{ width: `${58 + index * 7}%` }} />
              </div>
            </div>
          ))}
        </div>

        <div className="mp-card">
          <h3><Calendar size={11} aria-hidden="true" /> Weekly activity</h3>
          <div className="mp-heatmap" aria-hidden="true">
            {heatmap.map((level, index) => (
              <span key={index} className={level ? `level-${level}` : ''} />
            ))}
          </div>
        </div>

        <div className="mp-card mp-card--last">
          <h3><RotateCcw size={11} aria-hidden="true" /> Today&apos;s revision</h3>
          <div className="mp-revision-row">
            <div><strong>25</strong><span>Flashcards</span></div>
            <div><strong>12</strong><span>Bookmarked</span></div>
            <div><strong>6</strong><span>To review</span></div>
          </div>
        </div>
      </div>
    </article>
  );
}

function DashboardPreviewCard() {
  const navigate = useNavigate();
  const preview = HERO_PREVIEW;
  return (
    <article className="desktop-mockup-window dashboard-preview-card">
      {/* Desktop Window Title Bar Chrome */}
      <div className="desktop-window-bar">
        <div className="window-controls">
          <span className="window-dot dot-close" />
          <span className="window-dot dot-minimize" />
          <span className="window-dot dot-maximize" />
        </div>
        <div className="window-url-bar">
          <Lock size={12} className="url-lock-icon" aria-hidden="true" />
          <span className="url-protocol">https://</span>
          <span className="url-domain">{DOMAIN}</span>
          <span className="url-path">/dashboard</span>
        </div>
        <div className="window-actions-dummy">
          <span className="window-tab active">Dashboard</span>
          <span className="window-tab">Mocks</span>
        </div>
      </div>

      {/* Desktop Window Content Body */}
      <div className="desktop-window-body">
        <div className="preview-top">
          <div>
            <span>{preview.greeting}</span>
            <h2>{preview.name}</h2>
          </div>
          <div className="streak-chip">
            <Flame size={16} aria-hidden="true" />
            {preview.streakDays} day streak
          </div>
        </div>

        <div className="preview-metrics">
          <MetricTile label="Today's Goal" value={`${preview.todaysGoalPercent}%`} icon={Target} />
          <MetricTile
            label="Average Accuracy"
            value={`${preview.averageAccuracyPercent}%`}
            icon={Percent}
          />
        </div>

        <div className="continue-card">
          <div>
            <span>Continue Learning</span>
            <strong>{preview.continueTopic}</strong>
          </div>
          <button
            className="mini-action"
            onClick={() => navigate('/learn')}
            aria-label="Continue learning"
          >
            <Play size={17} aria-hidden="true" />
          </button>
        </div>

        <div className="preview-columns">
          <div>
            <h3>Weak Units</h3>
            {preview.weakUnits.map((unit) => (
              <div className="tiny-row" key={unit}>
                <span>{unit}</span>
                <span className="tiny-dot" />
              </div>
            ))}
          </div>
          <div>
            <h3>Upcoming Revision</h3>
            {preview.upcomingRevision.map((item) => (
              <div className="revision-pill" key={item.topic}>
                <span>{item.topic}</span>
                <small>{item.due}</small>
              </div>
            ))}
          </div>
        </div>

        <div className="skeleton-strip" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
      </div>
    </article>
  );
}

function MetricTile({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon: LucideIcon;
}) {
  return (
    <div className="metric-tile">
      <Icon size={18} aria-hidden="true" />
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function SubjectsScreen() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeSection, setActiveSection] = useState<'CUET_UG' | 'CUET_PG'>('CUET_UG');
  
  const { data: subjects, isLoading, isError, error, refetch } = useAcademicSubjects();

  const q = query.trim().toLowerCase();
  
  const filtered = (subjects ?? []).filter((s) => {
    const isPg = s.slug.startsWith('pg-') || s.code?.includes('QP') || s.name.includes('(PG)');
    const matchesSection = activeSection === 'CUET_PG' ? isPg : !isPg;
    const matchesQuery = !q || s.name.toLowerCase().includes(q) || (s.description ?? '').toLowerCase().includes(q) || (s.code ?? '').toLowerCase().includes(q);
    return matchesSection && matchesQuery;
  });

  return (
    <section className="subjects-screen adaptive-page">
      <div className="subjects-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <span className="section-kicker">Official NTA Catalog</span>
          <h1>Choose your CUET (UG) & CUET (PG) subject.</h1>
        </div>
        <Illustration context="page.home" size="sm" style={{ maxHeight: '110px' }} />
        <label className="search-field">
          <Search size={18} aria-hidden="true" />
          <span className="sr-only">Search subjects</span>
          <input
            placeholder={activeSection === 'CUET_UG' ? "Search 37 CUET (UG) subjects (e.g. 501, 305, Economics)..." : "Search CUET (PG) subjects (e.g. COQP12, SCQP24, Physics)..."}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
      </div>

      <SectionSwitcher activeSection={activeSection} onChange={setActiveSection} />

      {isLoading && <LoadingState label="Loading subjects…" />}

      {isError && (
        <ErrorState
          message={error instanceof Error ? error.message : 'Could not load subjects.'}
          onRetry={() => refetch()}
        />
      )}

      {!isLoading && !isError && filtered.length === 0 && (
        <EmptyState
          title={q ? 'No matching subjects' : 'No subjects in this section'}
          message={q ? 'Try a different search term.' : 'Subjects will appear here once they are published.'}
          illustrationContext="empty.search"
        />
      )}

      {!isLoading && !isError && filtered.length > 0 && (
        <div className="subject-grid">
          {filtered.map((subject) => (
            <SubjectCard
              key={subject.id}
              subject={subject}
              onOpen={() => {
                analyticsEvents.subjectSelected(subject.id);
                navigate(`/syllabus/${subject.slug}`);
              }}
            />
          ))}
        </div>
      )}

      <div className="subjects-banner glass-surface">
        <Illustration context="support.help" size="xs" style={{ maxHeight: '70px', marginRight: '16px' }} />
        <div>
          <span>Can't find your subject?</span>
          <strong>We cover all official CUET (UG) & CUET (PG) subjects.</strong>
        </div>
        <button className="primary-action compact" onClick={() => navigate('/syllabus')}>
          Browse Full Syllabus
        </button>
      </div>
    </section>
  );
}

function SubjectCard({ subject, onOpen }: { subject: AcademicSubject; onOpen: () => void }) {
  const isPg = subject.slug.startsWith('pg-') || subject.code?.includes('QP') || subject.name.includes('(PG)');
  const badgeLabel = subject.isCommon ? 'General Test (501)' : isPg ? `CUET (PG) • Code ${subject.code || 'PG'}` : `CUET (UG) • Code ${subject.code || 'UG'}`;

  return (
    <button
      className={`subject-card-v2 glass-surface ${subject.isCommon ? 'is-common' : isPg ? 'is-pg' : 'is-ug'}`}
      onClick={onOpen}
      style={{ position: 'relative', overflow: 'hidden' }}
    >
      <div className="card-glare-overlay" aria-hidden="true" />
      <div className="subject-card-head">
        <span className="subject-badge">{badgeLabel}</span>
      </div>
      <h2>{subject.name}</h2>
      {subject.description && <p>{subject.description}</p>}
      <div className="subject-card-meta">
        <span>
          <BookOpen size={15} aria-hidden="true" />
          View syllabus
        </span>
      </div>
      <Illustration
        context={subject.isCommon ? 'card.course' : isPg ? 'feature.timeline' : 'card.document'}
        size="xs"
        style={{
          position: 'absolute',
          right: '10px',
          bottom: '10px',
          opacity: 0.25,
          width: '75px',
          pointerEvents: 'none',
        }}
      />
    </button>
  );
}

function LearnPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Learn');
  return (
    <LearningWorkspace
      activeTab={activeTab}
      onTabChange={setActiveTab}
      onBack={() => navigate('/subjects')}
    />
  );
}

function LearningWorkspace({
  activeTab,
  onTabChange,
  onBack,
}: {
  activeTab: string;
  onTabChange: (tab: string) => void;
  onBack: () => void;
}) {
  const tabs = ['Learn', 'Notes', 'Mind Map', 'Practice'];

  return (
    <section className="learning-workspace">
      <header className="learning-topbar">
        <button className="ghost-icon-button" onClick={onBack} aria-label="Back to subjects">
          <ChevronLeft size={20} aria-hidden="true" />
        </button>
        <div className="learning-title">
          <span>Paper I</span>
          <strong>Research Aptitude</strong>
        </div>
        <button className="ghost-icon-button" aria-label="Bookmark topic">
          <Bookmark size={20} aria-hidden="true" />
        </button>
      </header>

      <div className="topic-progress">
        <span>Progress</span>
        <div className="progress-track">
          <span style={{ width: '64%' }} />
        </div>
        <strong>64%</strong>
      </div>

      <div className="learning-tabs" role="tablist" aria-label="Learning workspace tabs">
        {tabs.map((tab) => (
          <button
            key={tab}
            className={activeTab === tab ? 'is-active' : ''}
            onClick={() => onTabChange(tab)}
            role="tab"
            aria-selected={activeTab === tab}
          >
            {tab}
          </button>
        ))}
      </div>

      <article className="learning-content">
        {activeTab === 'Learn' && (
          <>
            <span className="section-kicker">Concept</span>
            <h1>Sampling Methods in Educational Research</h1>
            <p>
              Sampling is the process of selecting a representative group from a larger population.
              For CUET UG & PG, focus on probability sampling, non-probability sampling, sampling error,
              and how each method affects validity.
            </p>
            <div className="concept-grid">
              {['Simple Random', 'Stratified', 'Cluster', 'Purposive'].map((item) => (
                <div className="concept-card" key={item}>
                  <strong>{item}</strong>
                  <span>High-frequency exam concept</span>
                </div>
              ))}
            </div>
          </>
        )}
        {activeTab === 'Notes' && (
          <>
            <span className="section-kicker">Notes</span>
            <h1>Recall Pack</h1>
            <div className="note-stack">
              <p>Probability sampling gives every unit a known chance of selection.</p>
              <p>Stratified sampling improves representation when subgroups matter.</p>
              <p>Purposive sampling is common in qualitative research designs.</p>
            </div>
          </>
        )}
        {activeTab === 'Mind Map' && (
          <>
            <span className="section-kicker">Mind Map</span>
            <h1>Sampling at a glance</h1>
            <div className="mind-map">
              <strong>Sampling</strong>
              <span>Probability</span>
              <span>Non-probability</span>
              <span>Error</span>
              <span>Validity</span>
            </div>
          </>
        )}
        {activeTab === 'Practice' && (
          <>
            <span className="section-kicker">Practice</span>
            <h1>Quick Drill</h1>
            <div className="practice-card">
              <strong>Which method is best when subgroup representation is important?</strong>
              {['Cluster sampling', 'Stratified sampling', 'Snowball sampling'].map((option) => (
                <button key={option}>{option}</button>
              ))}
            </div>
          </>
        )}
      </article>

      <div className="learning-actions">
        <button className="secondary-action">
          <ChevronLeft size={17} aria-hidden="true" />
          Previous
        </button>
        <button className="primary-action compact">
          Next
          <ChevronRight size={17} aria-hidden="true" />
        </button>
      </div>
    </section>
  );
}

function TestsScreen() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<'CUET_UG' | 'CUET_PG'>('CUET_UG');

  const ugTests = [
    {
      code: '501',
      tag: 'General Test (501)',
      title: 'General Aptitude Test (GAT) Full Mock',
      desc: '60 Questions on Quantitative Aptitude, Logical Reasoning, GK & Language',
      duration: '60 Minutes',
      qCount: '60 Questions',
    },
    {
      code: '309',
      tag: 'Domain Paper (309)',
      title: 'Economics / Business Economics Full Length Mock',
      desc: 'Microeconomics, Macroeconomics, Indian Economy & Quantitative Drills',
      duration: '45 Minutes',
      qCount: '50 Questions',
    },
    {
      code: '305',
      tag: 'Domain Paper (305)',
      title: 'Business Studies Official Pattern Mock',
      desc: 'Principles of Management, Marketing, Finance & Business Environment',
      duration: '45 Minutes',
      qCount: '50 Questions',
    },
    {
      code: '301',
      tag: 'Domain Paper (301)',
      title: 'Accountancy / Book-Keeping Practice Test',
      desc: 'Financial Statements, Partnership Accounting & Computerized Accounting',
      duration: '45 Minutes',
      qCount: '50 Questions',
    },
    {
      code: '101',
      tag: 'Language (101)',
      title: 'English Language Comprehension Drill',
      desc: 'Reading Comprehension, Vocabulary, Grammar & Verbal Ability',
      duration: '45 Minutes',
      qCount: '50 Questions',
    },
    {
      code: 'PYQ',
      tag: 'Official NTA PYQs',
      title: 'CUET (UG) 2025/2024 Solved Question Papers',
      desc: 'Real NTA exam papers with step-by-step detailed explanations',
      duration: 'Adaptive',
      qCount: '100+ PYQs',
    },
  ];

  const pgTests = [
    {
      code: 'COQP12',
      tag: 'Common Paper (COQP12)',
      title: 'General – Management (PG) Full Mock',
      desc: 'Management Foundations, OB, Marketing, Finance & Strategic Decision Making',
      duration: '105 Minutes',
      qCount: '75 Questions',
    },
    {
      code: 'COQP08',
      tag: 'Common Paper (COQP08)',
      title: 'Commerce (PG) Comprehensive Series',
      desc: 'Corporate Accounting, Business Economics, Taxation & Financial Management',
      duration: '105 Minutes',
      qCount: '75 Questions',
    },
    {
      code: 'HUQP09',
      tag: 'Humanities (HUQP09)',
      title: 'History (PG) Exam Simulation',
      desc: 'Ancient, Medieval, Modern Indian History & Historiography',
      duration: '105 Minutes',
      qCount: '75 Questions',
    },
    {
      code: 'SCQP24',
      tag: 'Science (SCQP24)',
      title: 'Physics (PG) Full Length Test',
      desc: 'Mechanics, Quantum Physics, Electromagnetism & Thermodynamics',
      duration: '105 Minutes',
      qCount: '75 Questions',
    },
    {
      code: 'SCQP19',
      tag: 'Science (SCQP19)',
      title: 'Mathematics (PG) Advanced Sprint',
      desc: 'Real Analysis, Linear Algebra, Differential Equations & Modern Algebra',
      duration: '105 Minutes',
      qCount: '75 Questions',
    },
    {
      code: 'PYQ_PG',
      tag: 'Official NTA PYQs',
      title: 'CUET (PG) 2025/2024 Previous Year Solved Papers',
      desc: 'Year-wise original NTA papers with detailed solution keys',
      duration: 'Adaptive',
      qCount: '150+ PYQs',
    },
  ];

  const activeTests = activeSection === 'CUET_UG' ? ugTests : pgTests;

  return (
    <section className="tests-screen adaptive-page">
      <div className="section-heading">
        <span className="section-kicker">NTA CUET (UG) & CUET (PG) Test Series</span>
        <h1>Exam-mode practice with instant score analytics.</h1>
      </div>

      <SectionSwitcher
        activeSection={activeSection}
        onChange={setActiveSection}
        ugLabel="CUET (UG) Mocks"
        pgLabel="CUET (PG) Mocks"
      />

      <div className="test-grid">
        {activeTests.map((test) => (
          <article className="mock-card" key={test.title}>
            <div className="mock-icon">
              <ClipboardList size={24} aria-hidden="true" />
            </div>
            <span style={{
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: '6px',
              backgroundColor: '#fff4ed',
              color: '#ea580c',
              fontSize: '0.75rem',
              fontWeight: '700',
              marginBottom: '8px'
            }}>{test.tag}</span>
            <h2>{test.title}</h2>
            <p>{test.desc}</p>
            <div style={{ display: 'flex', gap: '12px', fontSize: '0.8rem', color: '#64748b', margin: '12px 0 16px', fontWeight: '600', alignItems: 'center' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Clock size={14} /> {test.duration}</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><ClipboardList size={14} /> {test.qCount}</span>
            </div>
            <button className="primary-action compact" onClick={() => navigate('/assessment')}>
              Start Test
              <ArrowRight size={17} aria-hidden="true" />
            </button>
          </article>
        ))}
      </div>
    </section>
  );
}

interface AiChatSession {
  id: string;
  title: string;
  updatedAt: string;
  messages: AiChatMessage[];
}

function AIWorkspace() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<AiChatSession[]>(() => {
    try {
      const saved = localStorage.getItem('webfoxx_ai_sessions');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [activeSessionId, setActiveSessionId] = useState<string | null>(() => {
    const saved = localStorage.getItem('webfoxx_ai_sessions');
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed.length > 0) return parsed[0].id;
    }
    return null;
  });

  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const { status } = useAuth();
  const location = useLocation();
  const [selectedModel, setSelectedModel] = useState('GPT-4o');
  const [usage, setUsage] = useState<AiUsageData>({ usedMessages: 0, maxMessages: 10, isPremium: false, aiCredits: 0.0, outOfQuota: false });

  const [showAddCreditsModal, setShowAddCreditsModal] = useState(false);
  const [selectedPack, setSelectedPack] = useState({ amount: 5, msgs: 20, price: 6.00, title: 'Starter Pack' });
  const [isPurchasing, setIsPurchasing] = useState(false);

  const refetchUsage = () => {
    if (status !== 'authenticated') {
      setUsage({ usedMessages: 0, maxMessages: 10, isPremium: false, aiCredits: 0.0, outOfQuota: false });
      return;
    }
    getAiUsage()
      .then((data) => {
        if (data && typeof data.usedMessages === 'number') {
          setUsage(data);
        }
      })
      .catch((err) => console.error('Failed to load AI usage from server:', err));
  };

  React.useEffect(() => {
    refetchUsage();
    if (status === 'authenticated') {
      getAiSessions()
        .then(async (dbSessions) => {
          // If the backend has no sessions, check if we have local legacy ones to migrate
          if (dbSessions.length === 0) {
            const localSaved = localStorage.getItem('webfoxx_ai_sessions');
            if (localSaved) {
              try {
                const parsed = JSON.parse(localSaved);
                if (parsed.length > 0) {
                  console.log('Migrating local AI sessions to backend database...');
                  // Save them to the backend in parallel
                  const promises = parsed.map((s: any) =>
                    saveAiSession({
                      id: s.id,
                      title: s.title,
                      messages: s.messages,
                      updatedAt: s.updatedAt
                    }).catch((err) => console.error(`Failed to migrate session ${s.id}:`, err))
                  );
                  await Promise.all(promises);
                  // Refetch from database to ensure fresh state
                  const freshDbSessions = await getAiSessions();
                  setSessions(freshDbSessions);
                  if (freshDbSessions.length > 0) {
                    setActiveSessionId(freshDbSessions[0].id);
                  }
                  return;
                }
              } catch (e) {
                console.error('Failed to parse local sessions for migration:', e);
              }
            }
          }

          // Otherwise, just load what's in the DB
          setSessions(dbSessions);
          if (dbSessions.length > 0) {
            // Keep the active session if it still exists in the DB, otherwise default to first
            setActiveSessionId((prev) => {
              if (prev && dbSessions.some((s) => s.id === prev)) return prev;
              return dbSessions[0].id;
            });
          } else {
            setActiveSessionId(null);
          }
        })
        .catch((err) => console.error('Failed to fetch AI sessions from backend:', err));
    } else {
      // For guests, fallback to localStorage
      try {
        const saved = localStorage.getItem('webfoxx_ai_sessions');
        const parsed = saved ? JSON.parse(saved) : [];
        setSessions(parsed);
        if (parsed.length > 0) {
          setActiveSessionId(parsed[0].id);
        } else {
          setActiveSessionId(null);
        }
      } catch {
        setSessions([]);
        setActiveSessionId(null);
      }
    }
  }, [status]);

  const handleUpgradePlan = async () => {
    try {
      await simulateUpgrade(!usage.isPremium);
      refetchUsage();
    } catch (err) {
      console.error('Failed to upgrade plan:', err);
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const executePurchase = async () => {
    try {
      setIsPurchasing(true);

      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        alert('Razorpay SDK failed to load. Are you online?');
        setIsPurchasing(false);
        return;
      }

      // 1. Create order on backend
      const orderData = await createRazorpayCreditsOrder(selectedPack.amount);

      setIsPurchasing(false);

      // 2. Open Razorpay Checkout modal
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "WebFoxx Academy",
        description: `Purchase ${selectedPack.amount} AI Tutor Credits`,
        image: "/assets/webfoxx_academy_logo.png",
        order_id: orderData.order_id,
        handler: async function (response: any) {
          try {
            setIsPurchasing(true);
            await verifyRazorpayCreditsPayment({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
              amount: selectedPack.amount
            });
            setIsPurchasing(false);
            setShowAddCreditsModal(false);
            refetchUsage();
            alert(`Payment successful! Added ${selectedPack.amount} Credits to your account.`);
          } catch (err: any) {
            setIsPurchasing(false);
            alert(err.message || 'Payment verification failed');
          }
        },
        prefill: {
          name: '',
          email: '',
        },
        theme: {
          color: "#8b5cf6",
        },
      };

      const rzp = new (window as any).Razorpay(options);
      rzp.open();
    } catch (err: any) {
      console.error('Failed to execute credits purchase:', err);
      alert(err.message || 'Error executing credits purchase');
      setIsPurchasing(false);
    }
  };

  const currentSession = useMemo(() => {
    return sessions.find((s) => s.id === activeSessionId) || null;
  }, [sessions, activeSessionId]);

  const messages = useMemo(() => {
    return currentSession ? currentSession.messages : [];
  }, [currentSession]);

  const actionPills = [
    { label: 'Explain a Concept', icon: Wand2, color: 'purple', prompt: 'Explain General Aptitude Test (GAT 501) pattern and key scoring sections in a clean table comparison.' },
    { label: 'Create Study Plan', icon: Calendar, color: 'green', prompt: 'Create a 30-day CUET (UG & PG) study timetable with unit-wise revision milestones.' },
    { label: 'Summarize Notes', icon: FileText, color: 'blue', prompt: 'Summarize CUET (PG) Common Paper (COQP12) Management concepts with key formula tricks.' },
    { label: 'Solve Doubts', icon: HelpCircle, color: 'orange', prompt: 'Explain Microeconomics vs Macroeconomics key differences for CUET (UG 309) with examples.' },
    { label: 'Generate Quiz', icon: PenTool, color: 'pink', prompt: 'Generate 5 practice PYQs for CUET (PG) Commerce (COQP08) with step-by-step solutions.' },
    { label: 'Analyze Mock', icon: BarChart2, color: 'violet', prompt: 'Give me a structured step-by-step checklist to analyze weak domain paper mock scores.' },
  ];

  const messagesEndRef = React.useRef<HTMLDivElement | null>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  React.useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  const saveSessions = (updated: AiChatSession[]) => {
    setSessions(updated);
    try {
      localStorage.setItem('webfoxx_ai_sessions', JSON.stringify(updated));
    } catch (e) {
      console.error('Failed to save chat sessions to localStorage:', e);
    }

    if (status === 'authenticated') {
      updated.forEach((newS) => {
        const oldS = sessions.find((s) => s.id === newS.id);
        if (!oldS || JSON.stringify(oldS.messages) !== JSON.stringify(newS.messages) || oldS.title !== newS.title) {
          saveAiSession({
            id: newS.id,
            title: newS.title,
            messages: newS.messages,
            updatedAt: newS.updatedAt
          }).catch((err) => console.error(`Failed to save session ${newS.id} to backend:`, err));
        }
      });
    }
  };

  const startNewChat = () => {
    const newId = `session_${Date.now()}`;
    const newSession: AiChatSession = {
      id: newId,
      title: 'New Study Session',
      updatedAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      messages: [],
    };
    const updated = [newSession, ...sessions];
    saveSessions(updated);
    setActiveSessionId(newId);
  };

  const showHowToUseGuide = () => {
    const targetId = 'session_howto';
    const existing = sessions.find((s) => s.id === targetId);

    if (existing) {
      setActiveSessionId(targetId);
      return;
    }

    const DUMMY_EXPLANATION = `### 📖 How to Use WebFoxx AI Tutor Effectively

Welcome to your personal **CUET (UG) & CUET (PG)** Study Companion! I am here to help you master concepts, practice PYQs, and diagnose your test performance.

To get the most accurate and high-quality responses from me, follow these best practices:

---

#### 1. Be Specific and Context-Rich (Efficient & Accurate)
Avoid vague or single-word questions. Provide the subject, topic, and exactly what you need.
* **❌ Poor Prompt:** *"Tell me about demand."*
* **✅ Great Prompt:** *"Explain the **Law of Demand** in Economics for CUET PG, including its exceptions and a quick summary table."*

---

#### 2. Formatting Math and Scientific Equations
I render mathematical formulas beautifully using LaTeX. You can ask for step-by-step math derivations:
- **Inline Math**: Wrapped in single dollar signs, e.g. $x^2 + y^2 = z^2$ or $\\theta = 45^\\circ$
- **Display Math**: Wrapped in double dollar signs:
  $$
  x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}
  $$

---

#### 3. Requesting Visual Charts & Data
When you want to see trends, syllabus distributions, or comparative data, ask me to draw a chart.
For example, here is the subject-wise weightage for the CUET General Test (GAT 501):

\`\`\`chart
{
  "type": "pie",
  "title": "CUET GAT 501 Syllabus Weightage",
  "labels": ["Numerical Ability", "Logical Reasoning", "General Awareness", "English Language"],
  "values": [35, 30, 20, 15]
}
\`\`\`

---

#### 4. Try These Recommended Prompt Templates:
* **PYQ Explanations:** *"Explain this CUET 2024 Accounting PYQ: [Insert Question] and list the core concept behind it."*
* **Mock Test Diagnosis:** *"I scored 60/100 in my Physics mock test. My weakest unit was Electrostatics. Give me a 3-day revision plan."*
* **Flowcharts:** *"Provide a step-by-step flowchart of the **Photosynthesis Light Reactions** using arrow blocks."*`;

    const howToSession: AiChatSession = {
      id: targetId,
      title: '📖 How to Use AI Tutor',
      updatedAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      messages: [
        {
          role: 'user',
          text: 'How can I use WebFoxx AI Tutor effectively for my CUET preparation?'
        },
        {
          role: 'assistant',
          text: DUMMY_EXPLANATION
        }
      ]
    };

    const updated = [howToSession, ...sessions.filter((s) => s.id !== targetId)];
    saveSessions(updated);
    setActiveSessionId(targetId);
  };

  const deleteSession = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const updated = sessions.filter((s) => s.id !== id);
    saveSessions(updated);
    if (activeSessionId === id) {
      setActiveSessionId(updated.length > 0 ? updated[0].id : null);
    }
    if (status === 'authenticated') {
      deleteAiSession(id).catch((err) => console.error(`Failed to delete session ${id} from backend:`, err));
    }
  };

  const handleSend = async (customPrompt?: string) => {
    const promptToSend = (customPrompt || input).trim();
    if (!promptToSend || loading) return;

    if (status === 'guest') {
      const from = `${location.pathname}${location.search}${location.hash}`;
      navigate('/login', { state: { from } });
      return;
    }

    let targetId = activeSessionId;
    let baseSessions = [...sessions];

    if (!targetId || !baseSessions.some((s) => s.id === targetId)) {
      targetId = `session_${Date.now()}`;
      const newSession: AiChatSession = {
        id: targetId,
        title: promptToSend.length > 28 ? `${promptToSend.slice(0, 28)}...` : promptToSend,
        updatedAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        messages: [],
      };
      baseSessions = [newSession, ...baseSessions];
      setActiveSessionId(targetId);
    }

    const targetSession = baseSessions.find((s) => s.id === targetId)!;
    const userMessage: AiChatMessage = { role: 'user', text: promptToSend };
    const userUpdatedMessages = [...targetSession.messages, userMessage];

    // Immutable copy of sessions array with updated active session
    const userNextSessions: AiChatSession[] = baseSessions.map((s) => {
      if (s.id === targetId) {
        return {
          ...s,
          title: s.messages.length === 0 ? (promptToSend.length > 28 ? `${promptToSend.slice(0, 28)}...` : promptToSend) : s.title,
          updatedAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          messages: userUpdatedMessages,
        };
      }
      return { ...s };
    });

    saveSessions(userNextSessions);
    if (!customPrompt) setInput('');
    setLoading(true);

    try {
      const res = await sendAiChatMessage(promptToSend, userUpdatedMessages);
      const assistantMessage: AiChatMessage = { role: 'assistant', text: res.reply };
      const finalMessages = [...userUpdatedMessages, assistantMessage];

      const finalSessions: AiChatSession[] = userNextSessions.map((s) => {
        if (s.id === targetId) {
          return {
            ...s,
            messages: finalMessages,
          };
        }
        return { ...s };
      });

      saveSessions(finalSessions);
      if (res.usage) {
        setUsage(res.usage);
      }
    } catch (e) {
      let errorText = 'Sorry, I encountered a temporary connection issue. Please check your connection and try asking again!';
      if (e instanceof ApiError) {
        errorText = e.message;
        // Refetch usage stats to show paywall immediately
        refetchUsage();
      } else if (e instanceof Error) {
        errorText = e.message;
      }
      
      const errorMsg: AiChatMessage = {
        role: 'assistant',
        text: errorText,
      };
      const finalMessages = [...userUpdatedMessages, errorMsg];
      const finalSessions: AiChatSession[] = userNextSessions.map((s) => {
        if (s.id === targetId) {
          return {
            ...s,
            messages: finalMessages,
          };
        }
        return { ...s };
      });
      saveSessions(finalSessions);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text: string, index: number) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  return (
    <section className="ai-workspace-container">
      {/* Left Sidebar */}
      <aside className="ai-sidebar">
        <button className="new-chat-btn" onClick={startNewChat}>
          <Plus size={18} /> New Chat
        </button>

        <div className="history-section">
          {sessions.length === 0 ? (
            <div className="empty-history-notice">
              No saved chat history yet. Click <strong>+ New Chat</strong> to begin a session!
            </div>
          ) : (
            <div className="history-group">
              <span className="history-group-title">Chat History ({sessions.length})</span>
              {sessions.map((item) => (
                <button
                  key={item.id}
                  className={`history-item ${activeSessionId === item.id ? 'is-active' : ''}`}
                  onClick={() => setActiveSessionId(item.id)}
                >
                  <div className="history-item-content">
                    <MessageCircle size={15} />
                    <div className="history-item-text">
                      <strong>{item.title}</strong>
                      <small>{item.updatedAt}</small>
                    </div>
                  </div>
                  <button
                    className="history-del-btn"
                    title="Delete Chat"
                    onClick={(e) => deleteSession(item.id, e)}
                  >
                    <Trash2 size={13} />
                  </button>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sidebar Bottom Cards */}
        <div className="sidebar-bottom-cards">
          <div className="sidebar-card">
            <div className="card-icon-head">
              <div className="card-icon-badge">
                <GraduationCap size={18} />
              </div>
              <div className="card-title-text">
                <strong>Study Assistant</strong>
                <small>Upload notes, PYQs or mock tests for AI analysis</small>
              </div>
            </div>
            <button className="sidebar-card-btn outline" onClick={() => alert('Document upload tool ready!')}>
              <Upload size={14} /> Upload Documents
            </button>
          </div>

          <div className="sidebar-card">
            <div className="card-title-text" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong>AI Workplace Plan</strong>
                <span style={{
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '0.68rem',
                  fontWeight: 'bold',
                  background: usage.isPremium ? 'rgba(139, 92, 246, 0.18)' : 'rgba(244, 63, 94, 0.15)',
                  color: usage.isPremium ? '#a78bfa' : '#fb7185'
                }}>
                  {usage.isPremium ? 'Premium ✨' : 'Free 🎁'}
                </span>
              </div>
              <div className="usage-progress-bar" style={{ marginTop: 8 }}>
                <div
                  className="usage-progress-fill"
                  style={{
                    width: `${Math.min(100, Math.round((usage.usedMessages / (usage.maxMessages || 10)) * 100))}%`,
                    background: usage.usedMessages >= (usage.maxMessages || 10) ? '#ef4444' : 'var(--color-primary)'
                  }}
                ></div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', marginTop: 6, color: '#94a3b8' }}>
                <span>Used: {usage.usedMessages} / {usage.maxMessages || 10} messages</span>
              </div>
              {usage.isPremium && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: '0.78rem', marginTop: 4, color: '#94a3b8', borderTop: '1px dashed rgba(255,255,255,0.08)', paddingTop: 4 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>AI Credits:</span>
                    <strong style={{ color: '#10b981' }}>{usage.aiCredits?.toFixed(2) || '0.00'} credits</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginTop: 2 }}>
                    <span>Available Messages:</span>
                    <strong style={{ color: '#f8fafc' }}>{Math.floor((usage.aiCredits || 0) / 0.25)} msgs</strong>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: '0.66rem', opacity: 0.8, color: '#64748b', marginTop: 1 }}>
                    (1 message = 0.25 credits)
                  </div>
                </div>
              )}
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%', marginTop: 8 }}>
              {usage.isPremium ? (
                <button className="sidebar-card-btn outline" onClick={() => setShowAddCreditsModal(true)}>
                  <Plus size={13} /> Add Credits
                </button>
              ) : (
                <button className="sidebar-card-btn purple" onClick={handleUpgradePlan}>
                  <Crown size={13} /> Upgrade to Premium
                </button>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Main Chat Workspace */}
      <main className="ai-main-workspace">
        {/* Top Header */}
        <header className="ai-top-header">
          <div>
            <div className="ai-header-title">
              <h1>CUET UG & PG AI Tutor</h1>
              <span className="beta-pill">BETA</span>
            </div>
            <p>Your personal assistant for CUET UG & PG preparation</p>
          </div>
          <button className="how-to-btn" onClick={showHowToUseGuide}>
            <PlayCircle size={15} /> How to use AI Tutor
          </button>
        </header>

        {/* Quick Action Pills Row */}
        <div className="action-pills-row">
          {actionPills.map((pill) => {
            const IconComp = pill.icon;
            return (
              <button
                key={pill.label}
                className={`action-pill ${pill.color}`}
                onClick={() => handleSend(pill.prompt)}
                disabled={loading}
              >
                <IconComp size={15} /> {pill.label}
              </button>
            );
          })}
        </div>

        {/* Feed Area */}
        <div className="chat-feed-container">
          {/* Default Greeting / Capability Card if no messages yet in active session */}
          {messages.length === 0 && (
            <div className="msg-card-assistant">
              <div className="msg-head">
                <div className="bot-avatar">
                  <img src="/assets/webfoxx_academy_logo.png" alt="WebFoxx AI" />
                </div>
                <div className="bot-meta">
                  <strong>WebFoxx AI</strong>
                  <small>Ready to help</small>
                </div>
              </div>
              <div className="msg-body">
                <p style={{ margin: 0, fontWeight: 600 }}>
                  Hello! I'm <strong>WebFoxx AI</strong>, your personal CUET UG & PG AI Tutor.
                </p>
                <p style={{ margin: '6px 0 12px', fontSize: '0.85rem', color: 'var(--color-muted, #94a3b8)' }}>
                  Ask me anything about CUET (UG) & CUET (PG) papers, or choose a prompt above to get started:
                </p>

                <div className="feature-check-grid">
                  <div className="check-item"><span className="check-icon">✓</span> CUET (UG) 37 Papers (Languages, Domains, GAT 501)</div>
                  <div className="check-item"><span className="check-icon">✓</span> CUET (PG) 157 Papers (COQP, HUQP, SCQP, LAQP)</div>
                  <div className="check-item"><span className="check-icon">✓</span> General Aptitude Test (GAT 501) Math & Reasoning Drills</div>
                  <div className="check-item"><span className="check-icon">✓</span> Mock Test Score Analytics & Error Diagnosis</div>
                  <div className="check-item"><span className="check-icon">✓</span> NTA Official Solved PYQs & Solution Keys</div>
                  <div className="check-item"><span className="check-icon">✓</span> Concept Explanations & Quick Formula Summaries</div>
                </div>
              </div>
            </div>
          )}

          {/* Dynamic Feed Messages */}
          {messages.map((message, index) => (
            <div key={index} className={message.role === 'user' ? 'msg-card-user' : 'msg-card-assistant'}>
              {message.role === 'user' ? (
                <>
                  <div className="user-text">{message.text}</div>
                  <span className="user-meta-time">You</span>
                  <div className="user-avatar">AY</div>
                </>
              ) : (
                <>
                  <div className="msg-head">
                    <div className="bot-avatar">
                      <img src="/assets/webfoxx_academy_logo.png" alt="WebFoxx AI" />
                    </div>
                    <div className="bot-meta">
                      <strong>WebFoxx AI</strong>
                      <small>AI Tutor</small>
                    </div>
                  </div>
                  <div className="msg-body">
                    <RichText value={message.text} />
                  </div>
                  <div className="msg-actions-toolbar">
                    <button
                      className={`action-icon-btn ${copiedIndex === index ? 'copied' : ''}`}
                      onClick={() => handleCopy(message.text, index)}
                    >
                      <Copy size={14} /> {copiedIndex === index ? 'Copied' : ''}
                    </button>
                    <button className="action-icon-btn" title="Like response">
                      <ThumbsUp size={14} />
                    </button>
                    <button className="action-icon-btn" title="Dislike response">
                      <ThumbsDown size={14} />
                    </button>
                    <button
                      className="action-icon-btn"
                      title="Read Aloud"
                      onClick={() => {
                        if ('speechSynthesis' in window) {
                          const utterance = new SpeechSynthesisUtterance(message.text.replace(/[#*|_`~]/g, ''));
                          window.speechSynthesis.speak(utterance);
                        }
                      }}
                    >
                      <Volume2 size={14} />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}

          {loading && (
            <div className="msg-card-assistant">
              <div className="msg-head">
                <div className="bot-avatar">
                  <Bot size={20} />
                </div>
                <div className="bot-meta">
                  <strong>WebFoxx AI</strong>
                  <small>Thinking…</small>
                </div>
              </div>
              <div className="msg-body" style={{ fontStyle: 'italic', color: '#94a3b8' }}>
                Analyzing concept and generating formatted markdown explanation with tables...
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Bottom Input Dock */}
        {usage.outOfQuota ? (
          <div className="input-card-box paywall-card-box" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px 20px',
            textAlign: 'center',
            background: 'rgba(15, 23, 42, 0.65)',
            border: '1px solid rgba(239, 68, 68, 0.35)',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 0 12px rgba(239, 68, 68, 0.05)',
            backdropFilter: 'blur(16px)'
          }}>
            <div style={{
              background: 'rgba(239, 68, 68, 0.12)',
              color: '#ef4444',
              width: '42px',
              height: '42px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '12px'
            }}>
              <Lock size={20} />
            </div>
            
            {usage.isPremium ? (
              <>
                <h3 style={{ fontSize: '1.05rem', fontWeight: 'bold', margin: '0 0 6px 0', color: '#f8fafc' }}>
                  Premium Quota Limit Reached (100 / 100 Messages)
                </h3>
                <p style={{ fontSize: '0.85rem', color: '#94a3b8', margin: '0 0 16px 0', maxWidth: '440px', lineHeight: 1.5 }}>
                  You have consumed your monthly Premium quota of 100 messages. To continue chatting, please add credits to your account. Each subsequent message costs <strong>0.25 credits</strong> (4 messages per credit).
                </p>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <button onClick={() => setShowAddCreditsModal(true)} style={{
                    background: '#10b981',
                    color: '#fff',
                    border: 'none',
                    padding: '8px 18px',
                    borderRadius: '10px',
                    fontWeight: 'bold',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <Plus size={14} /> Add Credits
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 style={{ fontSize: '1.05rem', fontWeight: 'bold', margin: '0 0 6px 0', color: '#f8fafc' }}>
                  Free Message Limit Reached (10 / 10 Messages)
                </h3>
                <p style={{ fontSize: '0.85rem', color: '#94a3b8', margin: '0 0 16px 0', maxWidth: '440px', lineHeight: 1.5 }}>
                  You have reached your limit of 10 free messages. Upgrade to Premium to get 90 additional messages and access full mocks!
                </p>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <button onClick={handleUpgradePlan} style={{
                    background: 'linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)',
                    color: '#fff',
                    border: 'none',
                    padding: '8px 18px',
                    borderRadius: '10px',
                    fontWeight: 'bold',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <Crown size={14} /> Upgrade to Premium (Demo)
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="input-card-box">
            <div className="input-field-area">
              <input
                placeholder="Ask anything about CUET UG & PG..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                disabled={loading}
              />
            </div>

            <div className="input-toolbar-row">
              <div className="input-actions-left">
                <button className="tool-chip-btn" onClick={() => handleSend('Show me key shortcuts for Paper 1')}>
                  <Plus size={14} />
                </button>
                <button className="tool-chip-btn" onClick={() => alert('Upload document tool')}>
                  <Upload size={13} /> Upload
                </button>
                <button className="tool-chip-btn" onClick={() => alert('Attach image tool')}>
                  <ImageIcon size={13} /> Image
                </button>
                <button
                  className="tool-chip-btn"
                  onClick={() => {
                    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
                      alert('Listening... Speak your NTA CUET & CUET (PG) question clearly.');
                    } else {
                      alert('Voice input is supported in Chrome/Edge browsers.');
                    }
                  }}
                >
                  <Mic size={13} /> Voice
                </button>
              </div>

              <div className="input-controls-right">
                <select className="model-select" value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)}>
                  <option value="GPT-4o">GPT-4o ∨</option>
                  <option value="Gemini 2.5">Gemini 2.5 ∨</option>
                  <option value="OpenRouter">OpenRouter ∨</option>
                </select>

                <button className="send-action-btn" onClick={() => handleSend()} disabled={!input.trim() || loading}>
                  <Send size={16} />
                </button>
              </div>
            </div>
          </div>
        )}

        <p className="disclaimer-footer">
          AI responses may not always be accurate. Please verify important information.
        </p>
      </main>

      {showAddCreditsModal && (
        <div className="theme-modal-overlay">
          <div className="theme-modal-card">
            <div className="theme-modal-head">
              <div>
                <h3>Purchase AI Tutor Credits</h3>
                <p>Standard Rate: ₹1.20 per credit (1 message = 0.25 credits / ₹0.30)</p>
              </div>
              <button
                type="button"
                className="theme-modal-close"
                onClick={() => {
                  if (!isPurchasing) setShowAddCreditsModal(false);
                }}
              >
                ✕
              </button>
            </div>

            <div className="theme-modal-body">
              {isPurchasing ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '40px 0',
                  textAlign: 'center'
                }}>
                  <div className="spinner" style={{
                    width: '40px',
                    height: '40px',
                    border: '3px solid rgba(139, 92, 246, 0.1)',
                    borderTop: '3px solid #8b5cf6',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    marginBottom: '16px'
                  }}></div>
                  <style>{`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}</style>
                  <strong style={{ color: 'var(--color-text)', display: 'block', marginBottom: '4px' }}>
                    Securing Payment Transaction...
                  </strong>
                  <span style={{ fontSize: '0.8rem', color: 'var(--color-muted)' }}>
                    Please do not close this window or reload the page.
                  </span>
                </div>
              ) : (
                <>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '12px',
                    marginBottom: '20px'
                  }}>
                    {[
                      { amount: 5, msgs: 20, price: 6.00, title: 'Starter Pack' },
                      { amount: 10, msgs: 40, price: 12.00, title: 'Standard Pack' },
                      { amount: 50, msgs: 200, price: 60.00, title: 'Pro Pack' },
                      { amount: 100, msgs: 400, price: 120.00, title: 'Elite Pack' }
                    ].map((pack) => (
                      <div 
                        key={pack.amount}
                        onClick={() => setSelectedPack(pack)}
                        className={`theme-modal-pack${selectedPack.amount === pack.amount ? ' is-selected' : ''}`}
                        style={{ position: 'relative' }}
                      >
                        <div style={{ fontSize: '0.7rem', fontWeight: 'bold', color: 'var(--color-purple)', textTransform: 'uppercase', marginBottom: '4px' }}>
                          {pack.title}
                        </div>
                        <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--color-text)', marginBottom: '2px' }}>
                          {pack.amount} Credits
                        </div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--color-muted)', marginBottom: '10px' }}>
                          {pack.msgs} AI Messages
                        </div>
                        <div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#10b981' }}>
                          ₹{pack.price.toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="theme-modal-summary">
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span>Selected Pack:</span>
                      <strong style={{ color: 'var(--color-text)' }}>{selectedPack.amount} Credits ({selectedPack.msgs} Messages)</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Amount Payable:</span>
                      <strong style={{ color: '#10b981' }}>₹{selectedPack.price.toFixed(2)}</strong>
                    </div>
                  </div>

                  <button 
                    onClick={executePurchase}
                    style={{
                      width: '100%',
                      background: 'linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)',
                      color: '#fff',
                      border: 'none',
                      padding: '12px',
                      borderRadius: '12px',
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      boxShadow: '0 4px 15px rgba(124, 58, 237, 0.3)'
                    }}
                  >
                    Proceed to Payment (₹{selectedPack.price.toFixed(2)})
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function MockTestPage() {
  const navigate = useNavigate();
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);
  return (
    <MockTestScreen
      selectedAnswer={selectedAnswer}
      submitted={submitted}
      onSelect={setSelectedAnswer}
      onSubmit={() => setSubmitted(true)}
      onNext={() => {
        setSelectedAnswer(null);
        setSubmitted(false);
      }}
      onBack={() => navigate('/mock-tests')}
    />
  );
}

function MockTestScreen({
  selectedAnswer,
  submitted,
  onSelect,
  onSubmit,
  onNext,
  onBack,
}: {
  selectedAnswer: number | null;
  submitted: boolean;
  onSelect: (index: number) => void;
  onSubmit: () => void;
  onNext: () => void;
  onBack: () => void;
}) {
  return (
    <main className="mock-fullscreen">
      <header className="mock-topbar">
        <button className="ghost-icon-button" onClick={onBack} aria-label="Exit mock test">
          <ChevronLeft size={21} aria-hidden="true" />
        </button>
        <div className="timer-pill">
          <Clock size={17} aria-hidden="true" />
          42:18
        </div>
        <strong>Question 12 / 50</strong>
      </header>

      <section className="mock-question-card">
        <span className="section-kicker">Research Aptitude</span>
        <h1>What is the primary purpose of correlation analysis in research?</h1>
        <div className="option-list">
          {testOptions.map((option, index) => {
            const isCorrect = submitted && index === 0;
            const isSelected = selectedAnswer === index;
            return (
              <button
                key={option}
                className={`${isSelected ? 'is-selected' : ''} ${isCorrect ? 'is-correct' : ''}`}
                onClick={() => onSelect(index)}
              >
                <span>{String.fromCharCode(65 + index)}</span>
                {option}
              </button>
            );
          })}
        </div>
        {submitted && (
          <div className="answer-explanation">
            <strong>Correct Answer: A</strong>
            <p>
              Correlation analysis measures the strength and direction of association between two
              variables. It does not prove causation.
            </p>
          </div>
        )}
        <div className="mock-actions">
          <button className="secondary-action">Mark for Review</button>
          <button
            className="primary-action compact"
            onClick={submitted ? onNext : onSubmit}
            disabled={selectedAnswer === null}
          >
            {submitted ? 'Next Question' : 'Submit'}
          </button>
        </div>
      </section>

      <aside className="question-palette">
        {Array.from({ length: 15 }, (_, index) => (
          <button
            key={index + 1}
            className={index === 11 ? 'is-active' : index < 11 ? 'is-done' : ''}
            aria-label={`Question ${index + 1}`}
          >
            {index + 1}
          </button>
        ))}
      </aside>
    </main>
  );
}

export default App;
